import secrets from "k6/secrets";
import { AWSConfig, S3Client } from "https://jslib.k6.io/aws/0.14.0/s3.js";
import { buildLogLine, nowNano, pushToLoki } from "../internal/push.ts";
import type { Store } from "../types.ts";

export interface GCSConfig {
  bucket?: string;
}

/**
 * Creates a GCS storage strategy that uploads the screenshot to a
 * Google Cloud Storage bucket (via S3-compatible HMAC auth) and pushes
 * only a URL reference to Loki.
 */
export function gcs(config: GCSConfig = {}): Store {
  return {
    async upload(
      buffer: ArrayBuffer,
      uuid: string,
      caption: string,
    ): Promise<void> {
      const filename = `${uuid}.png`;

      const gcsAccessKey = await secrets.get("sm-screenshot-gcs-access-key");
      const gcsSecretKey = await secrets.get("sm-screenshot-gcs-secret-key");
      const gcsBucket = config.bucket ||
        (await secrets.get("sm-screenshot-gcs-bucket"));
      const lokiHost = await secrets.get("sm-screenshot-loki-host");
      const lokiAuth = await secrets.get("sm-screenshot-loki-auth");
      const creds = { host: lokiHost, auth: lokiAuth };

      const s3 = new S3Client(
        new AWSConfig({
          region: "auto",
          accessKeyId: gcsAccessKey,
          secretAccessKey: gcsSecretKey,
          endpoint: "storage.googleapis.com",
        }),
      );

      await s3.putObject(gcsBucket, filename, buffer, {
        contentType: "image/png",
      });

      const screenshotUrl =
        `https://storage.googleapis.com/${gcsBucket}/${filename}`;

      pushToLoki(
        [
          {
            stream: {
              source: "synthetic-monitoring-agent-screenshot",
              level: "info",
            },
            values: [
              [
                nowNano(),
                buildLogLine("info", "page screenshot", {
                  id: uuid,
                  screenshot_url: screenshotUrl,
                  caption,
                }),
              ],
            ],
          },
        ],
        uuid,
        creds,
      );
    },
  };
}
